import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { doc, onSnapshot, collection, getDocs, getDoc, query, where, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import useAuthStore from './store/authStore';
import { usePermissao } from './hooks/usePermissao';
import { useIsMobile } from './hooks/useIsMobile';
import { diasParaVencer } from './utils/verificarValidade';
import BuscaGlobal from './components/BuscaGlobal';
import BuscaGlobalMobile from './components/BuscaGlobalMobile';
import { BannerAnuncio } from './components/BannerAnuncio';
import AjudaWidget from './components/AjudaWidget';

const cor = '#B51013';

const IcoDash = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="6" height="6" rx="1" fill="currentColor"/><rect x="9" y="1" width="6" height="6" rx="1" fill="currentColor" opacity=".6"/><rect x="1" y="9" width="6" height="6" rx="1" fill="currentColor" opacity=".6"/><rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor" opacity=".3"/></svg>;
const IcoTrein = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/><path d="M8 5v4l2.5 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const IcoCert = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M5 6h6M5 9h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><circle cx="12" cy="13" r="2.5" stroke="currentColor" strokeWidth="1.3"/><path d="M10.5 13h-9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;
const IcoBib = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M5 6h6M5 9h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const IcoComp = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M8 2l1.5 3.5L13 6l-2.5 2.5.5 3.5L8 10.5 5 12l.5-3.5L3 6l3.5-.5L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>;
const IcoPesq = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M5 8h6M8 5v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const IcoColab = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><circle cx="6" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M1 13c0-2.76 2.24-5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="12" cy="11" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M10.5 11h3M12 9.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const IcoRel = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M2 4h12M2 8h8M2 12h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const IcoConf = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const IcoPerm = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><rect x="3" y="7" width="10" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M5 7V5a3 3 0 0 1 6 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const IcoMask = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M2 4h12M2 8h12M2 12h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="13" cy="12" r="2" stroke="currentColor" strokeWidth="1.3"/></svg>;
const IcoAudit = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M5 5h4M5 8h6M5 11h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><circle cx="12" cy="11" r="2" fill="currentColor"/></svg>;
const IcoSolic = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M5 8h6M8 5v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const IcoAgenda = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M5 1v3M11 1v3M2 7h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="5.5" cy="10.5" r="1" fill="currentColor"/><circle cx="8" cy="10.5" r="1" fill="currentColor"/></svg>;

const TITULOS = {
  '/dashboard': 'Dashboard',
  '/meus-treinamentos': 'Meus treinamentos',
  '/biblioteca': 'Biblioteca de cursos',
  '/competencias': 'Minhas competências',
  '/pesquisas': 'Pesquisas',
  '/colaboradores': 'Colaboradores',
  '/relatorios': 'Relatórios',
  '/configuracoes': 'Configurações',
  '/permissoes': 'Gestão de permissões',
  '/mascaras': 'Máscaras de treinamento',
  '/auditoria': 'Log de auditoria',
  '/perfil': 'Minha conta',
  '/agenda': 'Agenda de vencimentos',
  '/certificados': 'Meus certificados',
  '/notificacoes': 'Notificações',
  '/solicitacoes': 'Solicitações de matrícula',
  '/saude': 'Saúde do sistema',
  '/trilha': 'Minha trilha de aprendizado',
  '/plano': 'Plano anual de treinamentos',
};

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { pode, nivel } = usePermissao();
  const usuario = useAuthStore((s) => s.usuario);
  const perfil = useAuthStore((s) => s.perfil);
  const logout = useAuthStore((s) => s.logout);

  const [drawerAberto, setDrawerAberto] = useState(false);
  const [alertas, setAlertas] = useState([]);
  const [alertasEquipe, setAlertasEquipe] = useState([]);
  const [notifInApp, setNotifInApp] = useState([]);
  const [sinoAberto, setSinoAberto] = useState(false);
  const [branding, setBranding] = useState({ nomeEmpresa: 'JATEAR', sloganEmpresa: 'TRATAMENTO ANTICORROSIVO', logoUrl: '', corPrimaria: '#B51013', links: [] });

  useEffect(() => {
    setDrawerAberto(false);
  }, [location.pathname]);

  useEffect(() => {
    getDoc(doc(db, 'parametros', 'default')).then(snap => {
      const data = snap.exists() ? snap.data() : {};
      if (snap.exists()) {
        setBranding(prev => ({
          nomeEmpresa: data.nomeEmpresa || prev.nomeEmpresa,
          sloganEmpresa: data.sloganEmpresa || prev.sloganEmpresa,
          logoUrl: data.logoUrl || '',
          corPrimaria: data.corPrimaria || prev.corPrimaria,
          links: Array.isArray(data.links) ? data.links.filter(l => l.label && l.url) : prev.links,
        }));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!usuario) return;
    const unsub = onSnapshot(doc(db, 'progresso', usuario.uid), (snap) => {
      if (!snap.exists()) { setAlertas([]); return; }
      const trein = snap.data().treinamentos || {};
      const lista = [];
      Object.entries(trein).forEach(([nome, info]) => {
        if (info.status === 'vencido') lista.push({ nome, tipo: 'vencido', label: 'Vencido' });
        else if (info.status === 'concluido' && info.venceEm) {
          const dias = diasParaVencer(info.venceEm);
          if (dias !== null && dias <= 30 && dias > 0)
            lista.push({ nome, tipo: 'urgente', label: `Vence em ${dias}d` });
        }
      });
      setAlertas(lista);
    }, () => {});
    return () => unsub();
  }, [usuario]);

  // Notificações in-app (novos treinamentos atribuídos)
  useEffect(() => {
    if (!usuario) return;
    const unsub = onSnapshot(
      query(collection(db, 'notificacoes'), where('destinatarioUid', '==', usuario.uid), where('lida', '==', false)),
      (snap) => setNotifInApp(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    return () => unsub();
  }, [usuario]);

  const marcarNotifLidas = async () => {
    for (const n of notifInApp) {
      await updateDoc(doc(db, 'notificacoes', n.id), { lida: true }).catch(() => {});
    }
    setNotifInApp([]);
  };

  // Para gestores (nível 3+): carrega alertas da equipe
  useEffect(() => {
    if (!usuario || nivel < 3 || !perfil?.nome) return;
    const carregarEquipe = async () => {
      try {
        const colabSnap = await getDocs(query(collection(db, 'colaboradores'), where('gestor', '==', perfil.nome), where('status', '==', 'Ativo')));
        if (colabSnap.empty) return;
        const lista = [];
        const docs = colabSnap.docs.slice(0, 20);
        const resultados = await Promise.all(
          docs.map(d => {
            const colab = d.data();
            return getDocs(query(collection(db, 'progresso'), where('__name__', '==', colab.uid || d.id)))
              .then(pSnap => ({ colab, pSnap }));
          })
        );
        resultados.forEach(({ colab, pSnap }) => {
          pSnap.docs.forEach(pd => {
            const trein = pd.data().treinamentos || {};
            Object.entries(trein).forEach(([nome, info]) => {
              if (info.status === 'vencido') lista.push({ nome, colaborador: colab.nome, tipo: 'vencido' });
            });
          });
        });
        setAlertasEquipe(lista.slice(0, 10));
      } catch {}
    };
    carregarEquipe();
  }, [usuario, nivel, perfil]);

  const paginaAtiva = location.pathname.startsWith('/colaboradores/')
    ? '/colaboradores'
    : location.pathname.startsWith('/treinamento/')
    ? '/meus-treinamentos'
    : location.pathname;

  const titulo = TITULOS[paginaAtiva] || 'Jatear LMS';

  useEffect(() => {
    document.title = `${titulo} | Jatear LMS`;
  }, [titulo]);

  const menuPrincipal = [
    { rota: '/dashboard', label: 'Dashboard', icone: <IcoDash />, acao: 'verDashboard' },
    { rota: '/meus-treinamentos', label: 'Meus treinamentos', icone: <IcoTrein />, acao: 'verMeusTreinamentos' },
    { rota: '/biblioteca', label: 'Biblioteca de cursos', icone: <IcoBib />, acao: 'verBiblioteca' },
    { rota: '/competencias', label: 'Minhas competências', icone: <IcoComp />, acao: 'verCompetencias' },
    { rota: '/pesquisas', label: 'Pesquisas', icone: <IcoPesq />, acao: 'verPesquisas' },
    { rota: '/certificados', label: 'Certificados', icone: <IcoCert />, acao: 'verMeusTreinamentos' },
    { rota: '/trilha', label: 'Minha trilha', icone: <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M2 14c1-3 4-5 7-7M9 7l3-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="3" cy="13" r="1.5" fill="currentColor"/><circle cx="13" cy="3" r="1.5" fill="currentColor"/></svg>, acao: 'verMeusTreinamentos' },
  ];

  const menuGestao = [
    { rota: '/colaboradores', label: 'Colaboradores', icone: <IcoColab />, acao: 'verColaboradores' },
    { rota: '/agenda', label: 'Agenda', icone: <IcoAgenda />, acao: 'verAgenda' },
    { rota: '/plano', label: 'Plano anual', icone: <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M5 1v2M11 1v2M2 6h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M5 9h2M9 9h2M5 12h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>, acao: 'gerenciarCursos' },
    { rota: '/solicitacoes', label: 'Solicitações', icone: <IcoSolic />, acao: 'verColaboradores' },
    { rota: '/relatorios', label: 'Relatórios', icone: <IcoRel />, acao: 'verRelatorios' },
    { rota: '/mascaras', label: 'Máscaras', icone: <IcoMask />, acao: 'gerenciarMascaras' },
    { rota: '/configuracoes', label: 'Configurações', icone: <IcoConf />, acao: 'verConfiguracoes' },
    { rota: '/permissoes', label: 'Permissões', icone: <IcoPerm />, acao: 'verPermissoes' },
    { rota: '/auditoria', label: 'Auditoria', icone: <IcoAudit />, acao: 'verAuditoria' },
    { rota: '/saude', label: 'Saúde do sistema', icone: <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M2 8h3l2-4 2 8 2-4 1 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M13 8h1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>, acao: 'verAuditoria' },
  ];

  const itensPrincipal = menuPrincipal.filter(m => pode(m.acao));
  const itensGestao = menuGestao.filter(m => pode(m.acao));

  const nomeExibido = perfil?.nome || usuario?.email || '';
  const iniciais = nomeExibido.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

  const SidebarContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <img
          src={branding.logoUrl || '/logomarca.png'}
          alt={branding.nomeEmpresa}
          style={{ height: '44px', maxWidth: '160px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
          onError={e => { e.target.src = '/logomarca.png'; }}
        />
        {isMobile && (
          <button onClick={() => setDrawerAberto(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '18px', padding: '4px', flexShrink: 0 }}>✕</button>
        )}
      </div>

      {/* Menu */}
      <div style={{ padding: '10px 0', flex: 1, overflowY: 'auto' }}>
        {itensPrincipal.length > 0 && (
          <>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.45)', padding: '10px 16px 4px', letterSpacing: '1px' }}>PRINCIPAL</div>
            {itensPrincipal.map(m => (
              <MenuItem key={m.rota} icone={m.icone} label={m.label} ativo={paginaAtiva === m.rota} onClick={() => navigate(m.rota)} />
            ))}
          </>
        )}
        {itensGestao.length > 0 && (
          <>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.45)', padding: '10px 16px 4px', letterSpacing: '1px' }}>GESTÃO</div>
            {itensGestao.map(m => (
              <MenuItem key={m.rota} icone={m.icone} label={m.label} ativo={paginaAtiva === m.rota} onClick={() => navigate(m.rota)} />
            ))}
          </>
        )}
      </div>

      {/* Nível + usuário */}
      <div>
        <div style={{ padding: '4px 16px 8px' }}>
          <span style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '8px', background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)' }}>
            Nível {nivel}
          </span>
        </div>
        <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {perfil?.fotoUrl ? (
            <img onClick={() => navigate('/perfil')} src={perfil.fotoUrl} alt={iniciais} title="Minha conta" style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover', cursor: 'pointer', flexShrink: 0, border: '1.5px solid rgba(255,255,255,0.4)' }} />
          ) : (
            <div onClick={() => navigate('/perfil')} title="Minha conta" style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff', flexShrink: 0, fontWeight: '600', cursor: 'pointer' }}>
              {iniciais}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => navigate('/perfil')}>
            <div style={{ fontSize: '11px', color: '#fff', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {perfil?.nome || usuario?.email}
            </div>
            {perfil?.funcao && <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', marginTop: '1px' }}>{perfil.funcao}</div>}
          </div>
          <div onClick={logout} title="Sair" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', flexShrink: 0 }}>✕</div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'Arial, sans-serif' }}>

      {/* SIDEBAR DESKTOP */}
      {!isMobile && (
        <div className="print-hide" style={{ width: '224px', background: branding.corPrimaria, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <SidebarContent />
        </div>
      )}

      {/* DRAWER MOBILE — backdrop */}
      {isMobile && drawerAberto && (
        <div onClick={() => setDrawerAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 199 }} />
      )}

      {/* DRAWER MOBILE — sidebar */}
      {isMobile && (
        <div style={{
          position: 'fixed', top: 0, left: 0, bottom: 0,
          width: '260px', background: branding.corPrimaria, zIndex: 200,
          transform: drawerAberto ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s ease',
          display: 'flex', flexDirection: 'column',
        }}>
          <SidebarContent />
        </div>
      )}

      {/* CONTEÚDO PRINCIPAL */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f5f5f5', minWidth: 0 }}>

        {/* Header */}
        <div className="print-hide" style={{ background: '#fff', borderBottom: '1px solid #e5e5e5', padding: isMobile ? '10px 14px' : '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
            {isMobile && (
              <button onClick={() => setDrawerAberto(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M3 6h14M3 10h14M3 14h14" stroke="#333" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            )}
            <div style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: '600', color: '#1a1a1a', flexShrink: 0 }}>{titulo}</div>
            {!isMobile && <BuscaGlobal />}
            {isMobile && <BuscaGlobalMobile />}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Sininho */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => setSinoAberto(v => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', position: 'relative' }}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M10 2a6 6 0 0 0-6 6v3l-1.5 2.5h15L16 11V8a6 6 0 0 0-6-6z" stroke={alertas.length > 0 ? cor : '#aaa'} strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M8.5 16.5a1.5 1.5 0 0 0 3 0" stroke={alertas.length > 0 ? cor : '#aaa'} strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {(alertas.length + alertasEquipe.length + notifInApp.length) > 0 && (
                  <span style={{ position: 'absolute', top: 0, right: 0, width: '14px', height: '14px', borderRadius: '50%', background: cor, color: '#fff', fontSize: '8px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {alertas.length + alertasEquipe.length + notifInApp.length}
                  </span>
                )}
              </button>

              {sinoAberto && (
                <div style={{ position: 'absolute', top: '32px', right: 0, width: '280px', background: '#fff', borderRadius: '10px', border: '1px solid #e5e5e5', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 300 }}>
                  <div style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0', fontSize: '12px', fontWeight: '600', color: '#1a1a1a', display: 'flex', justifyContent: 'space-between' }}>
                    Notificações
                    <span onClick={() => setSinoAberto(false)} style={{ cursor: 'pointer', color: '#aaa' }}>✕</span>
                  </div>
                  {alertasEquipe.length > 0 && (
                    <div style={{ padding: '6px 14px 2px', fontSize: '10px', fontWeight: '700', color: '#888', letterSpacing: '0.5px' }}>SUA EQUIPE</div>
                  )}
                  {alertasEquipe.map((a, i) => (
                    <div key={`eq-${i}`} onClick={() => { navigate('/colaboradores'); setSinoAberto(false); }} style={{ padding: '8px 14px', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', background: '#FCEBEB08' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#B51013', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '11px', fontWeight: '500', color: '#1a1a1a' }}>{a.colaborador}</div>
                        <div style={{ fontSize: '10px', color: '#B51013' }}>{a.nome} — Vencido</div>
                      </div>
                    </div>
                  ))}
                  {(alertas.length + alertasEquipe.length) === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: '#aaa' }}>Nenhum alerta</div>
                  ) : alertas.length > 0 && (
                    <div style={{ padding: '6px 14px 2px', fontSize: '10px', fontWeight: '700', color: '#888', letterSpacing: '0.5px' }}>SEUS TREINAMENTOS</div>
                  )}
                  {alertas.map((a, i) => (
                    <div key={i} onClick={() => { navigate('/meus-treinamentos'); setSinoAberto(false); }} style={{ padding: '10px 14px', borderBottom: i < alertas.length - 1 ? '1px solid #f5f5f5' : 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: a.tipo === 'vencido' ? cor : '#EF9F27', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '11px', fontWeight: '500', color: '#1a1a1a', lineHeight: '1.4' }}>{a.nome}</div>
                        <div style={{ fontSize: '10px', color: a.tipo === 'vencido' ? cor : '#EF9F27', marginTop: '1px' }}>{a.label}</div>
                      </div>
                    </div>
                  ))}
                  {notifInApp.length > 0 && (
                    <>
                      <div style={{ padding: '6px 14px 2px', fontSize: '10px', fontWeight: '700', color: '#888', letterSpacing: '0.5px' }}>NOVIDADES</div>
                      {notifInApp.map((n, i) => (
                        <div key={n.id} style={{ padding: '9px 14px', borderBottom: '1px solid #f5f5f5', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#378ADD', flexShrink: 0 }} />
                          <div style={{ flex: 1, fontSize: '11px', color: '#1a1a1a' }}>{n.mensagem}</div>
                        </div>
                      ))}
                      <div onClick={() => { marcarNotifLidas(); navigate('/meus-treinamentos'); setSinoAberto(false); }} style={{ padding: '8px 14px', fontSize: '11px', color: '#378ADD', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontWeight: '600', textAlign: 'center' }}>
                        Marcar como lidas e ver →
                      </div>
                    </>
                  )}
                  <div style={{ display: 'flex', borderTop: '1px solid #f0f0f0' }}>
                    <div onClick={() => { navigate('/meus-treinamentos'); setSinoAberto(false); }} style={{ flex: 1, padding: '10px 14px', textAlign: 'center', fontSize: '11px', color: cor, cursor: 'pointer', fontWeight: '600', borderRight: '1px solid #f0f0f0' }}>
                      Meus treinamentos →
                    </div>
                    <div onClick={() => { navigate('/notificacoes'); setSinoAberto(false); }} style={{ flex: 1, padding: '10px 14px', textAlign: 'center', fontSize: '11px', color: '#888', cursor: 'pointer' }}>
                      Histórico 🔔
                    </div>
                  </div>
                </div>
              )}
            </div>

            {!isMobile && <div style={{ fontSize: '12px', color: '#888' }}>Jatear Tratamento Anticorrosivo</div>}
          </div>
        </div>

        {/* Banners de anúncios */}
        <BannerAnuncio />

        {/* Conteúdo */}
        <div style={{ padding: isMobile ? '14px' : '24px', flex: 1, overflowY: 'auto' }}>
          <Outlet />
        </div>

        {/* Bottom nav mobile */}
        {isMobile && (
          <nav style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 150,
            background: '#fff', borderTop: '1px solid #e5e5e5',
            display: 'flex', justifyContent: 'space-around', alignItems: 'center',
            padding: '6px 0 calc(6px + env(safe-area-inset-bottom, 0px))',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.08)',
          }}>
            {[
              { rota: '/dashboard', icone: <IcoDash />, label: 'Início', acao: 'verDashboard' },
              { rota: '/meus-treinamentos', icone: <IcoTrein />, label: 'Treinos', acao: 'verMeusTreinamentos' },
              { rota: '/certificados', icone: <IcoCert />, label: 'Certif.', acao: 'verMeusTreinamentos' },
              { rota: '/pesquisas', icone: <IcoPesq />, label: 'Pesquisas', acao: 'verPesquisas' },
              { rota: '/perfil', icone: (
                <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: paginaAtiva === '/perfil' ? cor : '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '700', color: paginaAtiva === '/perfil' ? '#fff' : '#888' }}>
                  {iniciais.slice(0, 1)}
                </div>
              ), label: 'Conta', acao: null },
            ].filter(m => !m.acao || pode(m.acao)).map(m => (
              <button key={m.rota} onClick={() => navigate(m.rota)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 12px', color: paginaAtiva === m.rota ? cor : '#aaa', minWidth: '50px' }}>
                <span style={{ color: paginaAtiva === m.rota ? cor : '#aaa' }}>{m.icone}</span>
                <span style={{ fontSize: '9px', fontWeight: paginaAtiva === m.rota ? '700' : '400' }}>{m.label}</span>
              </button>
            ))}
          </nav>
        )}
        {/* Espaçador para o bottom nav não sobrepor conteúdo */}
        {isMobile && <div style={{ height: '56px' }} />}

        {/* Footer */}
        {!isMobile && branding.links.length > 0 && (
          <div className="print-hide" style={{ background: '#fff', borderTop: '1px solid #e5e5e5', padding: '8px 24px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {branding.links.map((l, i) => (
              <a key={i} href={l.url} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: '11px', color: '#aaa', cursor: 'pointer', textDecoration: 'none' }}
                onMouseEnter={e => e.target.style.color = branding.corPrimaria}
                onMouseLeave={e => e.target.style.color = '#aaa'}>
                {l.label}
              </a>
            ))}
          </div>
        )}
      </div>
      <AjudaWidget />
    </div>
  );
}

function MenuItem({ icone, label, ativo, onClick }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '10px 16px', cursor: 'pointer',
      color: ativo ? '#fff' : 'rgba(255,255,255,0.75)',
      background: ativo ? 'rgba(255,255,255,0.15)' : 'transparent',
      borderLeft: ativo ? '3px solid #fff' : '3px solid transparent',
      fontSize: '13px',
    }}>
      <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{icone}</span>
      {label}
    </div>
  );
}
